import { AskRequest } from '@hone-automation/common/lib/AskRequests';
import './AssignmentGroup.scss';
import { prefixClass } from 'lib/utils';
import RequestItem from '../RequestItem';
import RequestGroup from '../RequestGroup';
import { getGroupedDates, GroupData } from '../utils/dateUtils';
import { useQueueContext } from '../QueueContext';
import { useRef } from 'react';
import Tooltip from 'rsuite/Tooltip';
import { Whisper } from 'rsuite';
import { useQueryState } from 'hooks/useQueryState';

type AssignmentGroupProps = {
  me: boolean;
  blocker?: boolean;
  groupLabel?: string;
  requests: AskRequest[];
  reversed?: boolean;
  onClick?: (id: string) => void;
};

const sortRequests = (requests: AskRequest[], reversed: boolean) =>
  requests.sort((a, b) => {
    if (!reversed) {
      return new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime();
    } else {
      return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
    }
  });

const AssignmentGroup = ({ blocker, me, requests, groupLabel, onClick }: AssignmentGroupProps) => {
  const {
    state: { selectedItem, filters },
  } = useQueueContext();
  const prefix = prefixClass('assignment-group');
  const [requestId, setRequestId] = useQueryState('requestId');
  const status = filters.status;

  const groupDates = me
    ? sortRequests(requests, filters.sort === 'newest')
    : getGroupedDates(requests, filters.sort === 'newest');
  // Animation disabled - was distracting during filtering
  // const [parent] = useAutoAnimate({
  //   duration: 450,
  // });
  const parent = useRef(null);

  const handleClick = (id: string) => {
    if (id === '') return;
    if (onClick) {
      onClick(id);
      setRequestId(id);
    }
  };

  if (requests.length === 0) return null;

  const getShouldBeExapanded = (idx: number) => {
    if (status === 'Open' || status === 'In Progress') {
      return false;
    }
    return idx !== 0;
  };

  return (
    <div className={prefix()}>
      <div className={prefix('group-header')}>
        {/* <div className={prefix('group-header-label')}>{HEADER_STRINGS[me ? 'me' : 'team']}</div> */}
        {blocker ? (
          <Whisper
            placement="top"
            controlId="control-id-hover"
            trigger="hover"
            speaker={<Tooltip>These requests are blocking the delivery of financial statements</Tooltip>}
          >
            <div className={prefix('group-header-label')}>{groupLabel || 'Urgent Blockers'}</div>
          </Whisper>
        ) : (
          <div className={prefix('group-header-label')}>{groupLabel || 'Requests'}</div>
        )}
      </div>
      <div ref={parent} className={prefix('group-list')}>
        {me ? (
          <div className={prefix('group-list-me')}>
            {requests.map(request => (
              <RequestItem
                key={request.id}
                blocker={blocker}
                request={request}
                selected={request.id === selectedItem?.id}
                me={me}
                read={true}
                multiLocation={false}
                onClick={handleClick}
              />
            ))}
          </div>
        ) : (
          Object.keys(groupDates as { [k: string]: GroupData }).map((date, index) => (
            <RequestGroup
              key={`${date}-${index}`}
              blocker={blocker}
              collapsed={getShouldBeExapanded(index)}
              title={(groupDates as { [k: string]: GroupData })[date].label}
              requests={(groupDates as { [k: string]: GroupData })[date].requests}
              multiLocation={false}
              onRequestClick={handleClick}
            />
          ))
        )}
      </div>
    </div>
  );
};

export default AssignmentGroup;
